import { ThingEventSnapshot } from '@eagle/core-data-types';
import { Alert, Collapse, IconButton, Stack, styled, Table, TableBody, TableCell, TableContainer, TableRow } from '@ethings/ui-toolkit';
import { Box, useTheme } from '@mui/material';
import chroma from 'chroma-js';
import { FC, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, MiddleSpinner } from '../../components';
import { COLLAPSE_DEFAULT_TIME } from '../../constants';
import { useSearch } from '../../pages/list/use-search';
import { testid } from '../../util';
import { EntryDataBody, EventTableEventIcon } from './util';

interface EventsTableMobileViewProps {
  events: ThingEventSnapshot[];
  isLoading: boolean;
  paginationComponent?: JSX.Element;
  rowChildren?: (event: ThingEventSnapshot) => JSX.Element;
  tableHeight: string;
}

interface StyledTableContainerProps {
  tableHeight: string;
}

interface StyledTableBodyProps {
  isLoading: boolean;
}

const StyledTableBody = styled(TableBody, {
  shouldForwardProp: (prop) => prop !== 'isLoading',
})<StyledTableBodyProps>(({ isLoading }) => ({
  filter: isLoading ? 'blur(1px)' : '',
  opacity: isLoading ? 0.66 : 1,
}));

const StyledTableContainer = styled(TableContainer)<StyledTableContainerProps>(({ tableHeight }) => ({
  minHeight: tableHeight,
}));

const StyledTableRow = styled(TableRow)();
const StyledTableCell = styled(TableCell)();

const Row: FC<{ event: ThingEventSnapshot; rowChildren?: (event: ThingEventSnapshot) => JSX.Element }> = ({ event, rowChildren }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  return <>
    <StyledTableRow data-testid={testid`events-table-row-item-${event.eventId}`} sx={{ '& > *': { borderBottom: 'unset' } }}>
      <StyledTableCell sx={{ width: '5%', pl: .5, pr: 0 }}>
        <IconButton size="small" data-testid="expand-button" onClick={() => setOpen(!open)}>
          {open ? <Icon data-testid="icon-chevron-up" icon="ChevronUp" /> : <Icon data-testid="icon-chevron-down" icon="ChevronDown" />}
        </IconButton>
      </StyledTableCell>
      <StyledTableCell component="th" scope="row" sx={{ p: 1 }}>
        <EventTableEventIcon event={event} showDateBelowEvent />
      </StyledTableCell>
    </StyledTableRow>
    <StyledTableRow sx={{ transition: `borderBottom ${COLLAPSE_DEFAULT_TIME}ms linear` }} >
      <StyledTableCell sx={{ '&': { borderBottom: open ? `1px solid ${chroma(theme.palette.common.black).alpha(0.12).css()}` : 'none' }, py: 0, px: 1 }} colSpan={6}>
        <Collapse in={open} unmountOnExit>
          <div>
            <EntryDataBody event={event} />
            {rowChildren && rowChildren(event)}
          </div>
        </Collapse>
      </StyledTableCell>
    </StyledTableRow>
  </>;
};

export const EventsTableMobileView: FC<EventsTableMobileViewProps> = ({ events, isLoading, paginationComponent, rowChildren, tableHeight }) => {
  const { t } = useTranslation(['common']);
  const { filters } = useSearch();

  if (!events || !events.length) {
    if (isLoading) {
      return (
        <Stack minHeight={tableHeight}>
          <MiddleSpinner sx={{ height: 'auto', pt: '140px' }} />
        </Stack>
      );
    }
    return (
      <Stack minHeight={tableHeight}>
        <Box m={3}>
          {filters.length
            ? <Alert severity="info">{t('common:component.events.thing-event.labels.table.filter-no-results')}</Alert>
            : <Alert severity="info">{t('common:component.events.thing-event.labels.table.filter-no-selection')}</Alert>
          }
        </Box>
      </Stack>
    );
  }

  return (
    <StyledTableContainer tableHeight={tableHeight}>
      <Table data-testid="events-table">
        <StyledTableBody isLoading={isLoading}>
          {events.map((event) => <Row key={event.eventId} event={event} rowChildren={rowChildren} />)}
        </StyledTableBody>
      </Table>
      <Box display="flex" justifyContent="center" marginY={1}>
        {paginationComponent}
      </Box>
    </StyledTableContainer>
  );
};
